import { useEffect, useState } from "react"
import { AddDialog } from "@/components/User"
import {
  useGetOrganizationUsersApi,
  userRoleResponseToMember,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import { Member, Organization, User } from "@/types"
import { useTranslation } from "react-i18next"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface MemberDialogProps {
  organization: Organization
  currentMembers?: Member[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (newUsers: User[]) => void
}

const MemberDialog = ({
  organization,
  currentMembers = [],
  open,
  onOpenChange,
  onConfirm,
}: MemberDialogProps) => {
  const { alertToast } = useToast()
  const [selectableUsers, setSelectableUsers] = useState<User[]>([])
  const { t } = useTranslation()

  // APIs
  const {
    data: getOrganizationUsersResp,
    error: getOrganizationUsersError,
    fetchFn: getOrganizationUsers,
  } = useGetOrganizationUsersApi(organization.id)

  useEffect(() => {
    if (!open) return
    getOrganizationUsers()
  }, [open, getOrganizationUsers])

  useEffect(() => {
    if (!getOrganizationUsersResp || !getOrganizationUsersResp.data) return
    const users: User[] = []
    getOrganizationUsersResp.data.forEach((userRole) => {
      const user = userRoleResponseToMember(userRole)
      if (currentMembers.find((m) => m.id === user.id)) return
      users.push(user)
    })
    setSelectableUsers(users)
  }, [getOrganizationUsersResp, currentMembers])

  useEffect(() => {
    if (!getOrganizationUsersError) return
    console.error("Failed to get organization users", getOrganizationUsersError)

    alertToast({
      title: t("workspace.message.getOrgUsersFailed"),
      description: getUserFacingApiErrorMessage(
        getOrganizationUsersError,
        t,
        getOrganizationUsersResp?.message ||
          t("workspace.message.getOrgUsersFailed")
      ),
    })
  }, [getOrganizationUsersError, getOrganizationUsersResp, alertToast, t])

  const handleConfirm = (newUsers: User[]) => {
    if (newUsers.length === 0) {
      alertToast({
        title: t("workspace.message.noUsersSelected.title"),
        description: t("workspace.message.noUsersSelected.description"),
      })
      return
    }
    onConfirm(newUsers)
  }

  return (
    <AddDialog
      title={t("workspace.member.dialog.add.title")}
      description={t("workspace.member.dialog.add.description")}
      tableTitle={t("workspace.member.dialog.add.newMembers")}
      emptyMessage={t("workspace.member.dialog.add.noMembers")}
      currentUsers={currentMembers}
      selectableUsers={selectableUsers}
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={handleConfirm}
    />
  )
}

export { MemberDialog }
export default MemberDialog
